import type { RecordFormColumns, RecordFormField, RecordFormTab } from "features/shared/components/dynamic-form/types";
import type { TFunction } from "i18next";

import { DynamicRecordFormModal } from "features/shared/components/dynamic-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export type CatalogAddModalProps<TState extends Record<string, unknown>> = {
  buildConfig: (params: BuildCatalogFormConfigParams<TState>) => { fields: RecordFormField[]; tabs?: RecordFormTab[] };
  columns?: RecordFormColumns;
  createInitialState: () => TState;
  headerColumns?: RecordFormColumns;
  height?: number | string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (formState: TState) => void;
  open: boolean;
  title: string;
  width?: number | string;
};

type BuildCatalogFormConfigParams<TState> = {
  formState: TState;
  onFieldChange: (field: keyof TState & string, value: unknown) => void;
  t: TFunction;
};

export const CatalogAddModal = <TState extends Record<string, unknown>>({
  buildConfig,
  columns = 2,
  createInitialState,
  headerColumns,
  height,
  isSubmitting,
  onClose,
  onSubmit,
  open,
  title,
  width,
}: CatalogAddModalProps<TState>) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<TState>(createInitialState);

  useEffect(() => {
    if (!open) return;
    setFormState(createInitialState());
  }, [open, createInitialState]);

  const onFieldChange = useCallback((field: keyof TState & string, value: unknown) => {
    setFormState((current) => ({ ...current, [field]: value }));
  }, []);

  const { fields, tabs } = useMemo(
    () => buildConfig({ formState, onFieldChange, t }),
    [formState, buildConfig, onFieldChange, t]
  );

  return (
    <DynamicRecordFormModal
      columns={columns}
      fields={fields}
      headerColumns={headerColumns}
      height={height}
      isSubmitting={isSubmitting}
      mode="add"
      onClose={onClose}
      onSave={() => onSubmit(formState)}
      open={open}
      tabs={tabs}
      title={title}
      width={width}
    />
  );
};
